import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Search as SearchIcon, GraduationCap, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function Search() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get('q') || '';
  const [query, setQuery] = useState(q);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setQuery(q);
  }, [q]);

  useEffect(() => {
    if (!query.trim()) {
      setStudents([]);
      setTeachers([]);
      return;
    }
    setLoading(true);
    const term = query.trim().toLowerCase();
    const schoolId = user?.school_id;
    const academicYearId = schoolId ? localStorage.getItem(`selected_academic_year_id_${schoolId}`) : null;
    const params = {
      ...(schoolId ? { school_id: schoolId } : {}),
      ...(academicYearId ? { academic_year_id: academicYearId } : {}),
    };
    Promise.all([
      api.get('students', { params }).catch(() => ({ data: { data: [] } })),
      api.get('teachers', { params }).catch(() => ({ data: { data: [] } })),
    ])
      .then(([sRes, tRes]) => {
        const sList = sRes.data.data || [];
        const tList = tRes.data.data || [];
        setStudents(
          sList.filter(
            (s) =>
              (s.first_name && s.first_name.toLowerCase().includes(term)) ||
              (s.last_name && s.last_name.toLowerCase().includes(term)) ||
              (s.admission_no && s.admission_no.toLowerCase().includes(term)) ||
              (s.email && s.email.toLowerCase().includes(term))
          )
        );
        setTeachers(
          tList.filter(
            (t) =>
              (t.first_name && t.first_name.toLowerCase().includes(term)) ||
              (t.last_name && t.last_name.toLowerCase().includes(term)) ||
              (t.employee_id && t.employee_id.toLowerCase().includes(term)) ||
              (t.email && t.email.toLowerCase().includes(term))
          )
        );
      })
      .finally(() => setLoading(false));
  }, [query, user?.school_id]);

  useEffect(() => {
    const handler = () => {
      if (query.trim()) {
        setStudents([]);
        setTeachers([]);
        setLoading(true);
        const term = query.trim().toLowerCase();
        const schoolId = user?.school_id;
        const academicYearId = schoolId ? localStorage.getItem(`selected_academic_year_id_${schoolId}`) : null;
        const params = {
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        };
        Promise.all([
          api.get('students', { params }).catch(() => ({ data: { data: [] } })),
          api.get('teachers', { params }).catch(() => ({ data: { data: [] } })),
        ])
          .then(([sRes, tRes]) => {
            const sList = sRes.data.data || [];
            const tList = tRes.data.data || [];
            setStudents(
              sList.filter(
                (s) =>
                  (s.first_name && s.first_name.toLowerCase().includes(term)) ||
                  (s.last_name && s.last_name.toLowerCase().includes(term)) ||
                  (s.admission_no && s.admission_no.toLowerCase().includes(term)) ||
                  (s.email && s.email.toLowerCase().includes(term))
              )
            );
            setTeachers(
              tList.filter(
                (t) =>
                  (t.first_name && t.first_name.toLowerCase().includes(term)) ||
                  (t.last_name && t.last_name.toLowerCase().includes(term)) ||
                  (t.employee_id && t.employee_id.toLowerCase().includes(term)) ||
                  (t.email && t.email.toLowerCase().includes(term))
              )
            );
          })
          .finally(() => setLoading(false));
      }
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [query, user?.school_id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = (e.target.query && e.target.query.value) || query;
    if (value.trim()) navigate(`/search?q=${encodeURIComponent(value.trim())}`);
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-white">Search</h1>
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
          <Input
            name="query"
            type="search"
            placeholder="Search students, teachers..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 h-11 bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </form>
      {loading && <p className="text-slate-400">Searching...</p>}
      {!loading && query.trim() && (
        <>
          {students.length > 0 && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Students ({students.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {students.slice(0, 10).map((s) => (
                  <Link
                    key={s.id}
                    to={`/students/${s.id}`}
                    className="block p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-200"
                  >
                    {s.first_name} {s.last_name}
                    {s.admission_no && <span className="text-slate-500 ml-2">({s.admission_no})</span>}
                  </Link>
                ))}
                {students.length > 10 && (
                  <p className="text-sm text-slate-500">+ {students.length - 10} more</p>
                )}
              </CardContent>
            </Card>
          )}
          {teachers.length > 0 && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Teachers ({teachers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {teachers.slice(0, 10).map((t) => (
                  <Link
                    key={t.id}
                    to={`/teachers/${t.id}`}
                    className="block p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-200"
                  >
                    {t.first_name} {t.last_name}
                    {t.employee_id && <span className="text-slate-500 ml-2">({t.employee_id})</span>}
                  </Link>
                ))}
                {teachers.length > 10 && (
                  <p className="text-sm text-slate-500">+ {teachers.length - 10} more</p>
                )}
              </CardContent>
            </Card>
          )}
          {!loading && query.trim() && students.length === 0 && teachers.length === 0 && (
            <p className="text-slate-400">No results for &quot;{query}&quot;</p>
          )}
        </>
      )}
    </div>
  );
}
